import { useEffect, useCallback } from 'react';
import { MapContainer as LeafletMap, TileLayer, Marker, Polyline, Circle, Popup, ScaleControl, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const TILE_LAYERS = {
  gsi: {
    url: 'https://cyberjapandata.gsi.go.jp/xyz/std/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://maps.gsi.go.jp/">国土地理院</a>',
    maxZoom: 18,
  },
  opentopomap: {
    url: 'https://tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://opentopomap.org">OpenTopoMap</a>',
    maxZoom: 17,
  },
  osm: {
    url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://openstreetmap.org">OSM</a>',
    maxZoom: 19,
  },
};

const CATEGORY_COLORS = {
  mushroom: '#d97706',
  wild_plant: '#16a34a',
  landmark: '#f97316',
  danger: '#ef4444',
  water: '#3b82f6',
  shelter: '#8b5cf6',
  viewpoint: '#ec4899',
  campsite: '#059669',
};

function createWaypointIcon(category) {
  const color = CATEGORY_COLORS[category] || '#f97316';
  return L.divIcon({
    html: `<div style="width:26px;height:26px;background:${color};border-radius:50%;border:2.5px solid white;box-shadow:0 2px 10px rgba(0,0,0,0.4);"></div>`,
    className: '',
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  });
}

const userLocationIcon = L.divIcon({
  html: '<div class="user-location-marker"></div>',
  className: '',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

function MapClickHandler({ onMapClick, disabled }) {
  useMapEvents({
    click(e) {
      if (!disabled) {
        onMapClick({ lat: e.latlng.lat, lng: e.latlng.lng });
      }
    },
  });
  return null;
}

function MapInitializer({ onLocationFound }) {
  const map = useMap();
  useEffect(() => {
    map.locate({ setView: true, maxZoom: 14 });
    const handler = (e) => {
      onLocationFound({
        lat: e.latlng.lat,
        lng: e.latlng.lng,
        accuracy: e.accuracy,
        altitude: e.altitude,
      });
    };
    map.on('locationfound', handler);
    return () => map.off('locationfound', handler);
  }, [map, onLocationFound]);
  return null;
}

function FlyToLocation({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.flyTo([position.lat, position.lng], 15, { duration: 1 });
    }
  }, [map, position]);
  return null;
}

function BoundsTracker({ onBoundsChange }) {
  const map = useMap();
  useEffect(() => {
    const handler = () => {
      const b = map.getBounds();
      onBoundsChange({
        north: b.getNorth(),
        south: b.getSouth(),
        east: b.getEast(),
        west: b.getWest(),
        zoom: map.getZoom(),
      });
    };
    map.on('moveend', handler);
    map.on('zoomend', handler);
    handler();
    return () => {
      map.off('moveend', handler);
      map.off('zoomend', handler);
    };
  }, [map, onBoundsChange]);
  return null;
}

export default function MapContainerComponent({
  tileLayer = 'gsi',
  currentLocation,
  waypoints = [],
  trackPoints = [],
  selectedTrack,
  onMapClick,
  isRecording,
  flyTo,
  onWaypointClick,
  onBoundsChange,
  isOnline = true,
}) {
  const tile = TILE_LAYERS[tileLayer] || TILE_LAYERS.gsi;

  const handleLocationFound = useCallback((loc) => {
    // This is handled by MapInitializer
  }, []);

  return (
    <LeafletMap
      center={[35.36, 138.73]}
      zoom={10}
      zoomControl={false}
      attributionControl={false}
      style={{ height: '100dvh', width: '100%' }}
      data-testid="map-container"
    >
      <TileLayer key={`${tileLayer}-${isOnline}`} url={tile.url} attribution={tile.attribution} maxZoom={tile.maxZoom} />
      <ScaleControl position="bottomleft" imperial={false} />
      <MapClickHandler onMapClick={onMapClick} disabled={isRecording} />
      <MapInitializer onLocationFound={handleLocationFound} />
      {flyTo && <FlyToLocation position={flyTo} />}
      {onBoundsChange && <BoundsTracker onBoundsChange={onBoundsChange} />}

      {/* User location */}
      {currentLocation && (
        <>
          <Marker
            position={[currentLocation.lat, currentLocation.lng]}
            icon={userLocationIcon}
          />
          {currentLocation.accuracy && (
            <Circle
              center={[currentLocation.lat, currentLocation.lng]}
              radius={currentLocation.accuracy}
              pathOptions={{
                color: '#3b82f6',
                fillColor: '#3b82f6',
                fillOpacity: 0.1,
                weight: 1,
              }}
            />
          )}
        </>
      )}

      {/* Active recording track */}
      {trackPoints.length > 1 && (
        <Polyline
          positions={trackPoints.map((p) => [p.lat, p.lng])}
          pathOptions={{ color: '#f97316', weight: 4, opacity: 0.9 }}
        />
      )}

      {/* Selected historical track */}
      {selectedTrack?.points?.length > 1 && (
        <Polyline
          positions={selectedTrack.points.map((p) => [p.lat, p.lng])}
          pathOptions={{ color: '#84cc16', weight: 3, opacity: 0.8 }}
        />
      )}

      {/* Waypoints */}
      {waypoints.map((wp) => (
        <Marker
          key={wp.id}
          position={[wp.lat, wp.lng]}
          icon={createWaypointIcon(wp.category)}
          eventHandlers={{
            click: () => onWaypointClick && onWaypointClick(wp),
          }}
        >
          <Popup>
            <div style={{ minWidth: 140 }}>
              <strong style={{ fontSize: 14 }}>{wp.name}</strong>
              {wp.description && <p style={{ margin: '4px 0 0', opacity: 0.7, fontSize: 12 }}>{wp.description}</p>}
            </div>
          </Popup>
        </Marker>
      ))}
    </LeafletMap>
  );
}
